import React, { useEffect, useState } from "react";
import { useSiteContext } from "../SiteContext";
import {
  EmployeeTimeSheetWithDays,
  JobStatus,
  JobStatusEnum,
  JobProgressEmployee,
  RegularPiecePost,
  PayrollApprovedMiscEmployeeTask,
} from "../interfaces/V2Interface";
import VistaBatchExportManager from "../managers/VistaBatchExportManager";
import { generateBatchTaskCSVForEmployees, exportJobStatusToExcel } from "../api/vistaBatchExport";
import type { VistaExportTypeV2 } from "../api/vistaBatchExport";

interface VistaBatchExportV2ModalProps {
  visible: boolean;
  onClose: () => void;
  supervisorName: string;
  supervisorId: string;
  supervisorAltId: string;
  startDate: string;
  endDate: string;
  createPopup: (title: string, message: string) => void;
}

const LoadingSpinnerV2: React.FC<{ message: string }> = ({ message }) => (
  <div className="v2-batch-export-loading-container">
    <div className="v2-batch-export-loading-spinner"></div>
    <p className="v2-batch-export-loading-message">{message}</p>
  </div>
);

const ProgressDisplaySimpleV2: React.FC<{
  jobStatus: JobStatus;
  onShowDetails: () => void;
  translator: any;
}> = ({ jobStatus, onShowDetails, translator }) => (
  <div className="v2-batch-export-progress-container">
    <div className="v2-batch-export-progress-header">
      <h3>{translator.translate("ProcessingExport")}</h3>
      <div className={`v2-batch-export-job-status-badge v2-batch-export-job-status-${jobStatus.status.toLowerCase()}`}>
        {jobStatus.status}
      </div>
    </div>
    <div className="v2-batch-export-progress-bar-container">
      <div className="v2-batch-export-progress-bar">
        <div
          className="v2-batch-export-progress-fill"
          style={{ width: `${jobStatus.progress}%` }}
        />
      </div>
      <span className="v2-batch-export-progress-text">{jobStatus.progress}%</span>
    </div>
    <p className="v2-batch-export-current-step">{jobStatus.currentStep}</p>
    {jobStatus.status === JobStatusEnum.FAILED && (
      <div className="v2-batch-export-error-actions">
        <button className="v2-batch-export-details-button" onClick={onShowDetails}>
          {translator.translate("MoreDetails")}
        </button>
      </div>
    )}
  </div>
);

const CellStatusV2: React.FC<{
  completed: number;
  total: number;
  error?: string | object;
  label: string;
  onShowEmployeeError: (label: string, content: string) => void;
  translator: any;
}> = ({ completed, total, error, label, onShowEmployeeError, translator }) => {
  const totalVal = total ?? 0;
  const completedVal = completed ?? 0;
  const hasError = !!error;
  const isDone = totalVal > 0 && completedVal >= totalVal && !hasError;
  const isInProgress = totalVal > 0 && completedVal < totalVal && !hasError;

  const errorContent = error != null ? (typeof error === "string" ? error : JSON.stringify(error, null, 2)) : "";

  return (
    <td className="v2-batch-export-cell-status">
      <span className="v2-batch-export-cell-count">
        {completedVal}/{totalVal}
      </span>
      {isInProgress && (
        <span className="v2-batch-export-cell-spinner" title="In progress" aria-hidden />
      )}
      {isDone && (
        <span className="v2-batch-export-cell-ok" title="Completed" aria-hidden>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      )}
      {hasError && (
        <>
          <span className="v2-batch-export-cell-error-icon" title="Error" aria-hidden>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
              <path d="M5 5L11 11M11 5L5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </span>
          <button
            type="button"
            className="v2-batch-export-error-link"
            onClick={() => onShowEmployeeError(label, errorContent)}
          >
            {translator.translate("ViewError") || "View error"}
          </button>
        </>
      )}
    </td>
  );
};

const pieceClaimedUsd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

function formatPieceAmountClaimed(amount: string | number | undefined): string {
  if (amount == null || amount === "") return "—";
  const n = typeof amount === "number" ? amount : Number.parseFloat(String(amount));
  if (Number.isNaN(n)) return String(amount);
  return pieceClaimedUsd.format(n);
}

/** Index of closing brace/bracket matching the open at `openIdx`, or -1. Respects JSON string literals. */
function findMatchingStructuralEnd(s: string, openIdx: number, open: string, close: string): number {
  if (s[openIdx] !== open) return -1;
  let depth = 1;
  let inString = false;
  let escape = false;
  for (let j = openIdx + 1; j < s.length; j++) {
    const c = s[j];
    if (escape) {
      escape = false;
      continue;
    }
    if (inString) {
      if (c === "\\") escape = true;
      else if (c === '"') inString = false;
      continue;
    }
    if (c === '"') {
      inString = true;
      continue;
    }
    if (c === open) depth += 1;
    else if (c === close) {
      depth -= 1;
      if (depth === 0) return j;
    }
  }
  return -1;
}

function tryPrettyPrintJsonSlice(text: string, open: "{" | "[", close: "}" | "]"): { prefix: string; pretty: string } | null {
  const i = text.indexOf(open);
  if (i < 0) return null;
  const end = findMatchingStructuralEnd(text, i, open, close);
  if (end < 0) return null;
  const jsonStr = text.slice(i, end + 1);
  try {
    const parsed = JSON.parse(jsonStr) as unknown;
    return {
      prefix: text.slice(0, i).trimEnd(),
      pretty: JSON.stringify(parsed, null, 2),
    };
  } catch {
    return null;
  }
}

/** Pretty-print JSON when the error body is JSON or has a leading message followed by JSON. */
function formatErrorDetailsForDisplay(content: string): string {
  const trimmed = content.trim();
  if (!trimmed) return content;

  try {
    const parsed = JSON.parse(trimmed) as unknown;
    return JSON.stringify(parsed, null, 2);
  } catch {
    // not whole-string JSON
  }

  const asObject = tryPrettyPrintJsonSlice(content, "{", "}");
  if (asObject) {
    return asObject.prefix ? `${asObject.prefix}\n\n${asObject.pretty}` : asObject.pretty;
  }

  const asArray = tryPrettyPrintJsonSlice(content, "[", "]");
  if (asArray) {
    return asArray.prefix ? `${asArray.prefix}\n\n${asArray.pretty}` : asArray.pretty;
  }

  return content;
}

const PiecesProgressCellV2: React.FC<{
  completed: number;
  total: number;
  jobStatus: JobStatus;
  regularPiecePosts?: RegularPiecePost[];
  /** Present when the API reported an aggregate piece error but did not send per-post rows */
  piecesError?: string | null;
  onShowEmployeeError: (label: string, errorContent: string) => void;
  translator: any;
}> = ({ completed, total, jobStatus, regularPiecePosts, piecesError, onShowEmployeeError, translator }) => {
  const totalVal = total ?? 0;
  const completedVal = completed ?? 0;
  const jobRunning =
    jobStatus.status === JobStatusEnum.PENDING || jobStatus.status === JobStatusEnum.PROCESSING;
  const posts = regularPiecePosts ?? [];
  const hasAnyPiecePostFailed = posts.some((p) => p.status === "failed");
  const hasLegacyPiecesError = Boolean(piecesError?.trim());
  const hasAnyPieceError = hasAnyPiecePostFailed || hasLegacyPiecesError;
  const isDone = totalVal > 0 && completedVal >= totalVal && !hasAnyPieceError;
  const isInProgress = totalVal > 0 && completedVal < totalVal && jobRunning;
  const hasPosts = posts.length > 0;

  return (
    <td className="v2-batch-export-cell-status v2-batch-export-pieces-cell">
      <div className="v2-batch-export-pieces-cell-top">
        <span className="v2-batch-export-cell-count">
          {completedVal}/{totalVal}
        </span>
        {hasAnyPieceError && (
          <span
            className="v2-batch-export-cell-error-icon"
            title="One or more pieces have an error"
            aria-label="One or more pieces have an error"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
              <path d="M5 5L11 11M11 5L5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </span>
        )}
        {isInProgress && (
          <span className="v2-batch-export-cell-spinner" title="In progress" aria-hidden />
        )}
        {isDone && (
          <span className="v2-batch-export-cell-ok" title="Completed" aria-hidden>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
              <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        )}
      </div>
      {hasPosts && (
        <details className="v2-batch-export-piece-posts-details">
          <summary className="v2-batch-export-piece-posts-summary">
            Piece posts ({posts.length})
          </summary>
          <div className="v2-batch-export-piece-posts-table-wrap">
            <table className="v2-batch-export-piece-posts-table">
              <thead>
                <tr>
                  <th>OrderNo</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {posts.map((p) => {
                  const orderNo = p.vistaBody?.OrderNo;
                  const orderLabel = orderNo != null ? String(orderNo) : "—";
                  const failed = p.status === "failed";
                  const err = p.errorMessage?.trim() ?? "";
                  return (
                    <tr key={p.indexInEmployee}>
                      <td>{orderLabel}</td>
                      <td>{formatPieceAmountClaimed(p.vistaBody?.AmountClaimed)}</td>
                      <td>{p.status}</td>
                      <td>
                        {failed ? (
                          <button
                            type="button"
                            className="v2-batch-export-error-link v2-batch-export-piece-posts-error-link"
                            onClick={() =>
                              onShowEmployeeError(
                                orderNo != null ? `Piece ${orderNo}` : `Piece (#${p.indexInEmployee})`,
                                err || "(No error details)"
                              )
                            }
                          >
                            {translator.translate("ViewError") || "View error"}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </td>
  );
};

const ProgressDisplayDetailedV2: React.FC<{
  jobStatus: JobStatus;
  onShowDetails: () => void;
  onShowEmployeeError: (label: string, errorContent: string) => void;
  translator: any;
}> = ({ jobStatus, onShowDetails, onShowEmployeeError, translator }) => {
  const progressData = jobStatus.progressData;
  if (!progressData) return null;

  const employees = progressData.employees ?? [];

  return (
    <div className="v2-batch-export-progress-container v2-batch-export-progress-detailed">
      <div className="v2-batch-export-progress-header">
        <h3>{translator.translate("ProcessingExport")}</h3>
        <div className={`v2-batch-export-job-status-badge v2-batch-export-job-status-${jobStatus.status.toLowerCase()}`}>
          {jobStatus.status}
        </div>
      </div>
      <div className="v2-batch-export-progress-bar-container">
        <div className="v2-batch-export-progress-bar">
          <div
            className="v2-batch-export-progress-fill"
            style={{ width: `${jobStatus.progress}%` }}
          />
        </div>
        <span className="v2-batch-export-progress-text">{jobStatus.progress}%</span>
      </div>
      <p className="v2-batch-export-current-step">{jobStatus.currentStep}</p>

      {progressData.timecardBatchCreationError && (
        <div className="v2-batch-export-error-block">
          <strong>Timecard batch:</strong> {progressData.timecardBatchCreationError}
        </div>
      )}

      <div className="v2-batch-export-summary-grid">
        <div className="v2-batch-export-summary-item">
          <span className="v2-batch-export-summary-label">Time batch</span>
          <span>{progressData.completedTimeBatch ?? 0} / {progressData.totalTimeBatchEntries ?? 0}</span>
        </div>
        <div className="v2-batch-export-summary-item">
          <span className="v2-batch-export-summary-label">Pieces</span>
          <span>{progressData.completedPieces ?? 0} / {progressData.totalPieces ?? 0}</span>
        </div>
        <div className="v2-batch-export-summary-item">
          <span className="v2-batch-export-summary-label">Misc pieces</span>
          <span>{progressData.completedMiscPieces ?? 0} / {progressData.totalMiscPieces ?? 0}</span>
        </div>
      </div>

      <div className="v2-batch-export-employees-table-wrap">
        <table className="v2-batch-export-employees-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Time</th>
              <th>Pieces</th>
              <th>Misc Pieces</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((emp: JobProgressEmployee, idx: number) => (
              <tr key={emp.altEmployeeID ?? idx}>
                <td>
                  {emp.firstName} {emp.lastName} ({emp.altEmployeeID ?? ""})
                </td>
                <CellStatusV2
                  completed={emp.timeBatchCompleted ?? 0}
                  total={emp.timeBatchTotal ?? 0}
                  error={emp.timeBatchError}
                  label="Time"
                  onShowEmployeeError={onShowEmployeeError}
                  translator={translator}
                />
                <PiecesProgressCellV2
                  completed={emp.piecesCompleted ?? 0}
                  total={emp.piecesTotal ?? 0}
                  jobStatus={jobStatus}
                  regularPiecePosts={emp.regularPiecePosts}
                  piecesError={emp.piecesError}
                  onShowEmployeeError={onShowEmployeeError}
                  translator={translator}
                />
                <CellStatusV2
                  completed={emp.miscPiecesCompleted ?? 0}
                  total={emp.miscPiecesTotal ?? 0}
                  error={emp.miscPiecesError}
                  label="Misc Pieces"
                  onShowEmployeeError={onShowEmployeeError}
                  translator={translator}
                />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {jobStatus.status === JobStatusEnum.FAILED && (
        <div className="v2-batch-export-error-actions">
          <button className="v2-batch-export-details-button" onClick={onShowDetails}>
            {translator.translate("MoreDetails")}
          </button>
        </div>
      )}
    </div>
  );
};

const VistaBatchExportV2Modal: React.FC<VistaBatchExportV2ModalProps> = ({
  visible,
  onClose,
  supervisorName,
  supervisorId,
  supervisorAltId,
  startDate,
  endDate,
  createPopup,
}) => {
  const { payPeriodID, payPeriods, translator } = useSiteContext();
  const [employees, setEmployees] = useState<EmployeeTimeSheetWithDays[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<Set<string>>(new Set());
  const [employeeIDsWithUnapprovedMisc, setEmployeeIDsWithUnapprovedMisc] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(true);
  const [batchData, setBatchData] = useState<{
    batchId: number;
    keyID: number;
    totalEmployees: number;
    totalEntries: number;
    totalPieces: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchingEmployees, setFetchingEmployees] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [jobID, setJobID] = useState<number | null>(null);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [errorDetails, setErrorDetails] = useState<any>(null);
  const [showEmployeeErrorPopup, setShowEmployeeErrorPopup] = useState(false);
  const [employeeErrorContent, setEmployeeErrorContent] = useState<{ label: string; content: string } | null>(null);
  const [exportingStatusExcel, setExportingStatusExcel] = useState(false);

  const currentPayPeriod = payPeriods.find((pp) => pp.PayPeriodID === payPeriodID);
  const weekNumber = currentPayPeriod?.WeekNumber || 0;

  const formatName = (name: { first: string; middle?: string; last: string } | string) => {
    if (typeof name === "string") return name;
    return `${name.first} ${name.middle ? name.middle + " " : ""}${name.last}`;
  };

  // True if the employee has any red (misc task) nobody has acted on yet.
  //
  // THE TEST IS "IS SuperDescription BLANK", AND ONLY THAT. A blank description means
  // no approver has touched the red; anything written there means one has, whichever
  // workflow they used:
  //   - V1 misc-task page  — approve writes SuperDescription, leaves ApprovalStatus alone
  //   - two-level (TAS-360) — the level-2 approver is the one who writes SuperDescription
  //   - deny               — copies the deny reason in, and a denied red is terminal and
  //                          zeroed, so there is nothing left for anyone to wait on
  // So the one test answers correctly for all three, today and after NPM finishes moving
  // to the two-level flow.
  //
  // DO NOT REINTRODUCE AN ApprovalStatus CHECK. It was here (`status !== approved &&
  // status !== denied`) and it over-blocked badly: NPM runs two-level approval ON while
  // its approvers are still working the V1 page, so a normally-approved red today is
  // {ApprovalStatus: 1|2, SuperDescription: "..."}. The API exports that red — see
  // API/src/riceServices/timecards/timecardProcessor.ts, which ships a red on status 3
  // OR on not-denied-with-a-description — but this gate called it pending and locked the
  // whole employee out of the export.
  //
  // Known gap, deliberate: a force-approved red (OverrideMiscTaskApproval, status 3, no
  // description) blocks here where the API would export. Rare, and blocking is the safe
  // direction.
  //
  // KEEP THIS IN LOCKSTEP WITH VistaBatchExportModal.tsx. The two modals are
  // mounted side by side from SupervisorListPage and must agree on which employees
  // are exportable; a change to one and not the other is a silent split.
  const isMiscTaskPendingApproval = (misc: PayrollApprovedMiscEmployeeTask): boolean =>
    ((misc.superDescription ?? "") as string).trim() === "";

  const hasUnapprovedMiscTask = (emp: EmployeeTimeSheetWithDays): boolean => {
    const days = emp.days ?? [];
    for (const day of days) {
      const projectTasks = day.projectEmployeeTasks ?? [];
      for (const pt of projectTasks) {
        const miscTasks = pt.miscEmployeeTasks ?? [];
        for (const misc of miscTasks) {
          if (isMiscTaskPendingApproval(misc)) return true;
        }
      }
    }
    return false;
  };

  const resetModal = () => {
    setBatchData(null);
    setSelectedEmployees(new Set());
    setEmployeeIDsWithUnapprovedMisc(new Set());
    setSelectAll(true);
    setError(null);
    setFetchError(null);
    setEmployees([]);
    setFetchingEmployees(true);
    setLoading(false);
    setJobStatus(null);
    setJobID(null);
    setShowErrorDetails(false);
    setErrorDetails(null);
    setShowEmployeeErrorPopup(false);
    setEmployeeErrorContent(null);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const exportableEmployeeIDs = employees
    .filter((emp) => !employeeIDsWithUnapprovedMisc.has((emp.employeeID ?? "").toString()))
    .map((emp) => (emp.employeeID ?? "").toString());
  const exportableCount = exportableEmployeeIDs.length;

  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelectedEmployees(new Set());
    } else {
      setSelectedEmployees(new Set(exportableEmployeeIDs));
    }
    setSelectAll(!selectAll);
  };

  const handleCheckboxChange = (employeeID: string) => {
    setSelectedEmployees((prev) => {
      const updated = new Set(prev);
      updated.has(employeeID) ? updated.delete(employeeID) : updated.add(employeeID);
      setSelectAll(exportableCount > 0 && updated.size === exportableCount);
      return updated;
    });
  };

  const runBatchExportV2 = async (exportType: VistaExportTypeV2) => {
    if (selectedEmployees.size === 0) {
      setError(translator.translate("PleaseSelectEmployee"));
      createPopup(translator.translate("ExportError"), translator.translate("PleaseSelectEmployee"));
      return;
    }

    setLoading(true);
    setError(null);
    setJobStatus(null);
    setJobID(null);
    setErrorDetails(null);

    try {
      const employeeIDs = employees
        .filter(
          (emp) =>
            selectedEmployees.has((emp.employeeID ?? "").toString()) &&
            !employeeIDsWithUnapprovedMisc.has((emp.employeeID ?? "").toString())
        )
        .map((emp) => (emp.employeeID ?? "").toString());

      const id = await VistaBatchExportManager.generateVistaBatchExportV2(
        parseInt(supervisorId),
        payPeriodID,
        employeeIDs,
        exportType
      );
      setJobID(id);

      const result = await VistaBatchExportManager.pollJobStatus(id, (status) => setJobStatus(status));

      if ("error" in result) {
        setErrorDetails(result.error);
        const errorMessagePopup =
          result.error?.errorDetails
            ? `${translator.translate("ExportFailed")}\n\n${JSON.stringify(result.error.errorDetails, null, 2)}`
            : translator.translate("ExportFailed");
        setError(translator.translate("ExportFailed"));
        createPopup(translator.translate("ExportError"), errorMessagePopup);
      } else {
        setBatchData(result);
      }
    } catch (err) {
      console.error("Error in Vista batch export V2:", err);
      const errorMessage =
        err instanceof Error ? err.message : translator.translate("FailedCompleteBatchExport");
      setError(errorMessage);
      createPopup(translator.translate("ExportError"), translator.translate("FailedCompleteBatchExport"));
    } finally {
      setLoading(false);
    }
  };

  const handleExportTimeAndPieces = () => runBatchExportV2("both");
  const handleExportTimeOnly = () => runBatchExportV2("time");
  const handleExportPiecesOnly = () => runBatchExportV2("pieceworks");

  const handleGeneratePiecesCSV = async () => {
    if (selectedEmployees.size === 0) {
      setError(translator.translate("PleaseSelectEmployee"));
      createPopup(translator.translate("ExportError"), translator.translate("PleaseSelectEmployee"));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const employeeIDs = employees
        .filter(
          (emp) =>
            selectedEmployees.has((emp.employeeID ?? "").toString()) &&
            !employeeIDsWithUnapprovedMisc.has((emp.employeeID ?? "").toString())
        )
        .map((emp) => (emp.employeeID ?? "").toString());

      const year = currentPayPeriod?.AccountingYear ?? new Date().getFullYear();

      await generateBatchTaskCSVForEmployees(
        parseInt(supervisorId),
        payPeriodID,
        employeeIDs,
        year,
        weekNumber,
        supervisorAltId
      );
    } catch (err) {
      console.error("Error generating Pieces CSV:", err);
      const errorMessage =
        err instanceof Error ? err.message : translator.translate("FailedCompleteBatchExport");
      setError(errorMessage);
      createPopup(translator.translate("ExportError"), errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleShowErrorDetails = () => setShowErrorDetails(true);

  const handleShowEmployeeError = (label: string, content: string) => {
    setEmployeeErrorContent({ label, content });
    setShowEmployeeErrorPopup(true);
  };

  const handleExportStatusToExcel = async () => {
    if (jobID == null) return;
    setExportingStatusExcel(true);
    try {
      await exportJobStatusToExcel(jobID);
    } catch (err) {
      console.error("Error exporting status to Excel:", err);
      createPopup(
        translator.translate("ExportError"),
        err instanceof Error ? err.message : "Failed to download status Excel."
      );
    } finally {
      setExportingStatusExcel(false);
    }
  };

  useEffect(() => {
    if (visible && supervisorId && payPeriodID) {
      const fetchEmployees = async () => {
        try {
          setFetchError(null);
          setFetchingEmployees(true);

          const employeesData = await VistaBatchExportManager.getEmployees(supervisorId, payPeriodID);

          employeesData.sort((a: EmployeeTimeSheetWithDays, b: EmployeeTimeSheetWithDays) => {
            const altA = parseInt(a.altEmployeeID || "0", 10) || 0;
            const altB = parseInt(b.altEmployeeID || "0", 10) || 0;
            return altA - altB;
          });

          if (!employeesData || employeesData.length === 0) {
            throw new Error(translator.translate("NoEmployeesFoundSupervisor"));
          }

          setEmployees(employeesData);

          const unapprovedSet = new Set<string>();
          employeesData.forEach((emp: EmployeeTimeSheetWithDays) => {
            if (hasUnapprovedMiscTask(emp)) unapprovedSet.add((emp.employeeID ?? "").toString());
          });
          setEmployeeIDsWithUnapprovedMisc(unapprovedSet);

          const exportableIDs = employeesData
            .filter((emp: EmployeeTimeSheetWithDays) => !unapprovedSet.has((emp.employeeID ?? "").toString()))
            .map((emp: EmployeeTimeSheetWithDays) => (emp.employeeID ?? "").toString());
          setSelectedEmployees(new Set(exportableIDs));
          setSelectAll(exportableIDs.length > 0);
        } catch (err) {
          console.error("Failed to fetch data:", err);
          const errorMessage =
            err instanceof Error ? err.message : translator.translate("FailedFetchEmployeeData");
          setFetchError(errorMessage);
          createPopup(translator.translate("FetchError"), translator.translate("FailedFetchEmployeeData"));
          setEmployees([]);
          setSelectedEmployees(new Set());
        } finally {
          setFetchingEmployees(false);
        }
      };

      fetchEmployees();
    }
  }, [visible, supervisorId, payPeriodID, startDate, endDate, translator]);

  if (!visible) return null;

  const showingProgress = jobStatus != null;
  const isTerminal = jobStatus?.status === JobStatusEnum.COMPLETED || jobStatus?.status === JobStatusEnum.FAILED;

  return (
    <>
      <div className="v2-batch-export-modal-overlay">
        <div
          className={`v2-batch-export-modal ${showingProgress ? "v2-batch-export-modal--progress" : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          {fetchingEmployees ? (
            <LoadingSpinnerV2 message={translator.translate("LoadingEmployees")} />
          ) : showingProgress ? (
            <>
              {jobStatus!.progressData ? (
                <ProgressDisplayDetailedV2
                  jobStatus={jobStatus!}
                  onShowDetails={handleShowErrorDetails}
                  onShowEmployeeError={handleShowEmployeeError}
                  translator={translator}
                />
              ) : (
                <ProgressDisplaySimpleV2
                  jobStatus={jobStatus!}
                  onShowDetails={handleShowErrorDetails}
                  translator={translator}
                />
              )}
              {isTerminal && (
                <div className="v2-batch-export-final-status">
                  <h4 className="v2-batch-export-final-status-title">
                    {jobStatus!.status === JobStatusEnum.COMPLETED
                      ? "Process completed"
                      : "Process failed"}
                  </h4>
                  {jobStatus!.status === JobStatusEnum.COMPLETED && batchData && (
                    <div className="v2-batch-export-success v2-batch-export-final-success">
                      <div className="v2-batch-export-batch-ids v2-batch-export-batch-ids--horizontal">
                        <div className="v2-batch-export-field">
                          <label>Batch ID</label>
                          <input type="text" value={batchData.batchId} readOnly className="v2-batch-export-textbox" />
                        </div>
                        <div className="v2-batch-export-field">
                          <label>Key ID</label>
                          <input type="text" value={batchData.keyID} readOnly className="v2-batch-export-textbox" />
                        </div>
                        <div className="v2-batch-export-field">
                          <label>Total Employees</label>
                          <input type="text" value={batchData.totalEmployees} readOnly className="v2-batch-export-textbox" />
                        </div>
                        <div className="v2-batch-export-field">
                          <label>Total Time Entries</label>
                          <input type="text" value={batchData.totalEntries} readOnly className="v2-batch-export-textbox" />
                        </div>
                        <div className="v2-batch-export-field">
                          <label>Total Pieces</label>
                          <input type="text" value={batchData.totalPieces} readOnly className="v2-batch-export-textbox" />
                        </div>
                      </div>
                    </div>
                  )}
                  {jobStatus!.status === JobStatusEnum.FAILED && errorDetails != null && (
                    <div className="v2-batch-export-error-block">
                      <pre className="v2-batch-export-error-details-content v2-batch-export-final-error-pre">
                        {JSON.stringify(errorDetails, null, 2)}
                      </pre>
                      <button
                        type="button"
                        className="v2-batch-export-details-button"
                        onClick={handleShowErrorDetails}
                      >
                        {translator.translate("MoreDetails")}
                      </button>
                    </div>
                  )}
                  <div className="v2-batch-export-modal-footer">
                    {jobID != null && (
                      <button
                        className="v2-batch-export-button"
                        onClick={handleExportStatusToExcel}
                        disabled={exportingStatusExcel}
                      >
                        {translator.translate("ExportStatusToExcel") || "Export Status to Excel"}
                      </button>
                    )}
                    <button className="v2-batch-export-button" onClick={handleClose}>
                      {translator.translate("Close")}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : loading ? (
            <LoadingSpinnerV2 message={translator.translate("StartingExport")} />
          ) : showErrorDetails ? (
            <div className="v2-batch-export-error-details-container">
              <h3>{translator.translate("ErrorDetails")}</h3>
              <pre className="v2-batch-export-error-details-content">
                {JSON.stringify(errorDetails, null, 2)}
              </pre>
              <button
                className="v2-batch-export-button"
                onClick={() => setShowErrorDetails(false)}
              >
                {translator.translate("Back")}
              </button>
            </div>
          ) : (
          <>
            <h2 className="v2-batch-export-title">
              Vista Export V2 for {supervisorName}
            </h2>

            <div className="v2-batch-export-content">
              <p>{translator.translate("SupervisorID")} {supervisorAltId}</p>
              <p>{translator.translate("Week")} {weekNumber}</p>

              {fetchError && (
                <div className="v2-batch-export-error">
                  <p>{translator.translate("ErrorLoadingEmployees")} {fetchError}</p>
                </div>
              )}

              {error && !fetchError && <p className="v2-batch-export-error">{error}</p>}

              {employeeIDsWithUnapprovedMisc.size > 0 && (
                <p className="v2-batch-export-unapproved-misc-message">
                  {employeeIDsWithUnapprovedMisc.size}{" "}
                  {/* `translator.translate` returns undefined for a key that is not in
                      strings.json, which is why this literal was the only copy anyone
                      ever saw. The key now EXISTS (en + es), so the literal is a real
                      fallback again rather than the de-facto text. */}
                  {translator.translate("UnapprovedMiscTaskMessage") ||
                    "employee(s) have a miscellaneous task still awaiting approval and cannot be exported."}
                </p>
              )}
            </div>

            {employees.length > 0 && (
              <>
                <div className="v2-batch-export-select-all">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAllChange}
                    id="v2-batch-export-select-all"
                    disabled={loading || employees.length === 0 || exportableCount === 0}
                  />
                  <label htmlFor="v2-batch-export-select-all">
                    {translator.translate("SelectAll")} ({selectedEmployees.size} of {exportableCount}{" "}
                    {translator.translate("Selected")})
                  </label>
                </div>

                <div className="v2-batch-export-employee-list-container">
                  <ul className="v2-batch-export-employee-list">
                    {employees.map((emp) => {
                      const empIdStr = (emp.employeeID ?? "").toString();
                      const isUnapprovedMisc = employeeIDsWithUnapprovedMisc.has(empIdStr);
                      return (
                        <li
                          key={empIdStr}
                          className={`v2-batch-export-employee-item ${isUnapprovedMisc ? "v2-batch-export-employee-item-unapproved-misc" : ""}`}
                        >
                          {isUnapprovedMisc ? (
                            <span>
                              {formatName(emp.name)} ({translator.translate("EmployeeID")} {emp.altEmployeeID})
                            </span>
                          ) : (
                            <label>
                              <input
                                type="checkbox"
                                checked={selectedEmployees.has(empIdStr)}
                                onChange={() => handleCheckboxChange(empIdStr)}
                                disabled={loading}
                              />
                              {formatName(emp.name)} ({translator.translate("EmployeeID")} {emp.altEmployeeID})
                            </label>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </>
            )}

            <div className="v2-batch-export-modal-footer">
              {employees.length > 0 && (
                <>
                  <button
                    className="v2-batch-export-button"
                    onClick={handleExportTimeAndPieces}
                    disabled={loading || selectedEmployees.size === 0}
                  >
                    Export Time and Pieces (V2) ({selectedEmployees.size})
                  </button>
                  <button
                    className="v2-batch-export-button"
                    onClick={handleExportTimeOnly}
                    disabled={loading || selectedEmployees.size === 0}
                  >
                    Export Time Only (V2) ({selectedEmployees.size})
                  </button>
                  <button
                    className="v2-batch-export-button"
                    onClick={handleExportPiecesOnly}
                    disabled={loading || selectedEmployees.size === 0}
                  >
                    Export Pieces Only (V2) ({selectedEmployees.size})
                  </button>
                  <button
                    className="v2-batch-export-button"
                    onClick={handleGeneratePiecesCSV}
                    disabled={loading || selectedEmployees.size === 0}
                  >
                    Generate Pieces CSV ({selectedEmployees.size})
                  </button>
                </>
              )}
              <button
                className="v2-batch-export-cancel"
                onClick={handleClose}
                disabled={loading}
              >
                {employees.length === 0 ? translator.translate("Close") : translator.translate("Cancel")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>

      {showEmployeeErrorPopup && employeeErrorContent && (
        <div
          className="v2-batch-export-modal-overlay v2-batch-export-error-overlay"
          onClick={() => {
            setShowEmployeeErrorPopup(false);
            setEmployeeErrorContent(null);
          }}
        >
          <div
            className="v2-batch-export-modal v2-batch-export-error-popup-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>{employeeErrorContent.label} – {translator.translate("ErrorDetails")}</h3>
            <pre className="v2-batch-export-error-details-content">{formatErrorDetailsForDisplay(employeeErrorContent.content)}</pre>
            <button
              className="v2-batch-export-button"
              onClick={() => {
                setShowEmployeeErrorPopup(false);
                setEmployeeErrorContent(null);
              }}
            >
              {translator.translate("Close")}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const v2Styles = `
.v2-batch-export-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
}

.v2-batch-export-modal {
  background: #fff;
  padding: 30px;
  border-radius: 10px;
  width: 600px;
  max-width: 95%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  position: relative;
  display: flex;
  flex-direction: column;
}

.v2-batch-export-modal.v2-batch-export-modal--progress {
  width: 92%;
  max-width: 960px;
  max-height: 92vh;
}

.v2-batch-export-loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

.v2-batch-export-loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: v2-batch-export-spin 1s linear infinite;
  margin-bottom: 15px;
}

.v2-batch-export-loading-message {
  color: #666;
  font-size: 16px;
  margin: 0;
}

@keyframes v2-batch-export-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.v2-batch-export-progress-container {
  padding: 20px;
  text-align: left;
}

.v2-batch-export-progress-detailed {
  min-width: 520px;
}

.v2-batch-export-modal--progress .v2-batch-export-employees-table-wrap {
  max-height: 35vh;
  overflow-y: auto;
}

.v2-batch-export-cell-status {
  vertical-align: middle;
}

.v2-batch-export-cell-status .v2-batch-export-cell-count {
  margin-right: 6px;
}

.v2-batch-export-cell-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #e0e0e0;
  border-top-color: #1976d2;
  border-radius: 50%;
  animation: v2-batch-export-spin 0.8s linear infinite;
  vertical-align: middle;
}

.v2-batch-export-cell-ok {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  vertical-align: middle;
  color: #2e7d32;
}

.v2-batch-export-cell-ok svg {
  display: block;
}

.v2-batch-export-cell-error-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  vertical-align: middle;
  color: #b71c1c;
}

.v2-batch-export-cell-error-icon svg {
  display: block;
}

.v2-batch-export-progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.v2-batch-export-progress-header h3 {
  margin: 0;
  color: #333;
}

.v2-batch-export-job-status-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
}

.v2-batch-export-job-status-pending {
  background-color: #fff3cd;
  color: #856404;
}

.v2-batch-export-job-status-processing {
  background-color: #d1ecf1;
  color: #0c5460;
}

.v2-batch-export-job-status-completed {
  background-color: #d4edda;
  color: #155724;
}

.v2-batch-export-job-status-failed {
  background-color: #f8d7da;
  color: #721c24;
}

.v2-batch-export-progress-bar-container {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.v2-batch-export-progress-bar {
  flex: 1;
  height: 20px;
  background-color: #e0e0e0;
  border-radius: 10px;
  overflow: hidden;
}

.v2-batch-export-progress-fill {
  height: 100%;
  background-color: #4caf50;
  transition: width 0.3s ease;
}

.v2-batch-export-progress-text {
  font-weight: bold;
  color: #333;
  min-width: 40px;
}

.v2-batch-export-current-step {
  color: #666;
  font-size: 14px;
  margin: 0 0 12px 0;
}

.v2-batch-export-error-block {
  background-color: #fde8e8;
  color: #b71c1c;
  padding: 8px 10px;
  border-radius: 4px;
  margin-bottom: 12px;
  font-size: 14px;
}

.v2-batch-export-summary-grid {
  display: flex;
  gap: 20px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}

.v2-batch-export-summary-item {
  display: flex;
  flex-direction: column;
}

.v2-batch-export-summary-label {
  font-size: 12px;
  color: #666;
}

.v2-batch-export-employees-table-wrap {
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 12px;
}

.v2-batch-export-employees-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.v2-batch-export-employees-table th,
.v2-batch-export-employees-table td {
  padding: 8px 10px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.v2-batch-export-employees-table th {
  background-color: #f5f5f5;
  font-weight: 600;
}

.v2-batch-export-error-link {
  display: block;
  margin-top: 4px;
  padding: 0;
  background: none;
  border: none;
  color: #1976d2;
  cursor: pointer;
  font-size: 12px;
  text-decoration: underline;
}

.v2-batch-export-error-link:hover {
  color: #1565c0;
}

.v2-batch-export-final-status {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
}

.v2-batch-export-final-status-title {
  margin: 0 0 12px 0;
  font-size: 16px;
}

.v2-batch-export-final-success {
  margin-top: 0;
}

.v2-batch-export-final-error-pre {
  max-height: 200px;
  margin-bottom: 12px;
}

.v2-batch-export-modal-overlay.v2-batch-export-error-overlay {
  z-index: 1000;
}

.v2-batch-export-error-popup-modal {
  width: 560px;
  max-width: 95%;
}

.v2-batch-export-error-actions {
  margin-top: 15px;
}

.v2-batch-export-details-button {
  padding: 8px 16px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.v2-batch-export-details-button:hover {
  background-color: #c82333;
}

.v2-batch-export-error-details-container {
  padding: 20px;
}

.v2-batch-export-error-details-container h3 {
  margin-top: 0;
  color: #dc3545;
}

.v2-batch-export-error-details-content {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 15px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 300px;
  overflow-y: auto;
  margin: 15px 0;
}

.v2-batch-export-title {
  margin-top: 0;
  margin-bottom: 15px;
}

.v2-batch-export-content {
  margin-bottom: 15px;
}

.v2-batch-export-content p {
  margin: 4px 0;
}

.v2-batch-export-error {
  color: #d32f2f;
  background-color: #fde8e8;
  padding: 10px;
  border-radius: 4px;
  margin: 10px 0;
}

.v2-batch-export-unapproved-misc-message {
  color: #b71c1c;
  background-color: #ffebee;
  padding: 8px 10px;
  border-radius: 4px;
  margin: 10px 0;
  font-size: 14px;
}

.v2-batch-export-select-all {
  margin-bottom: 10px;
}

.v2-batch-export-select-all label {
  margin-left: 8px;
}

.v2-batch-export-employee-list-container {
  max-height: 200px;
  overflow-y: auto;
  margin-bottom: 15px;
}

.v2-batch-export-employee-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.v2-batch-export-employee-item {
  padding: 6px 0;
  border-bottom: 1px solid #eee;
}

.v2-batch-export-employee-item-unapproved-misc {
  background-color: #ffebee;
  padding: 6px 8px;
  margin: 0 -8px;
}

.v2-batch-export-success {
  margin: 20px 0;
  padding: 15px;
  background-color: #f8fff8;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
}

.v2-batch-export-batch-ids {
  margin-top: 15px;
}

.v2-batch-export-batch-ids--horizontal {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 10px 14px;
  margin-top: 6px;
  align-items: flex-end;
}

.v2-batch-export-batch-ids--horizontal .v2-batch-export-field {
  margin-bottom: 0;
  flex: 0 1 auto;
  min-width: 0;
}

.v2-batch-export-batch-ids--horizontal .v2-batch-export-field label {
  font-size: 11px;
  margin-bottom: 2px;
  font-weight: 600;
}

.v2-batch-export-batch-ids--horizontal .v2-batch-export-textbox {
  width: 104px;
  min-width: 104px;
  max-width: 128px;
  padding: 4px 6px;
  font-size: 13px;
  box-sizing: border-box;
}

.v2-batch-export-field {
  margin-bottom: 10px;
}

.v2-batch-export-field label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

.v2-batch-export-textbox {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #f9f9f9;
}

.v2-batch-export-modal-footer {
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  margin-top: 20px;
  flex-wrap: wrap;
}

.v2-batch-export-button {
  padding: 10px 20px;
  background-color: #1976d2;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.v2-batch-export-button:hover:not(:disabled) {
  background-color: #1565c0;
}

.v2-batch-export-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.v2-batch-export-cancel {
  padding: 10px 20px;
  background-color: #ccc;
  color: black;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.v2-batch-export-cancel:hover:not(:disabled) {
  background-color: #999;
}

.v2-batch-export-cancel:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.v2-batch-export-pieces-cell {
  vertical-align: top;
}

.v2-batch-export-pieces-cell-top {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.v2-batch-export-piece-posts-details {
  margin-top: 8px;
}

.v2-batch-export-piece-posts-summary {
  cursor: pointer;
  font-size: 12px;
  color: #1976d2;
  user-select: none;
  list-style: none;
}

.v2-batch-export-piece-posts-summary::-webkit-details-marker {
  display: none;
}

.v2-batch-export-piece-posts-table-wrap {
  max-height: 200px;
  overflow: auto;
  margin-top: 6px;
  border: 1px solid #eee;
  border-radius: 4px;
  background: #fff;
}

.v2-batch-export-piece-posts-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
}

.v2-batch-export-piece-posts-table th,
.v2-batch-export-piece-posts-table td {
  padding: 4px 6px;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}

.v2-batch-export-piece-posts-table th {
  background-color: #fafafa;
  font-weight: 600;
}

.v2-batch-export-piece-posts-error-link {
  display: inline !important;
  margin-top: 0 !important;
  white-space: nowrap;
}
`;

if (typeof document !== "undefined" && !document.getElementById("v2-batch-export-modal-styles")) {
  const styleTag = document.createElement("style");
  styleTag.id = "v2-batch-export-modal-styles";
  styleTag.innerHTML = v2Styles;
  document.head.appendChild(styleTag);
}

export default VistaBatchExportV2Modal;
